import { useDatoClient } from '@src/stores-v2/StoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useEffect, useState } from 'react';

import { toInternalEditorNameSet } from './contentItems';

const logger = LogManager.getLogger('hook:useArborInternalEditorNames');

const ARBOR_EMAIL_DOMAIN = 'tryarbor.com';

const EMPTY: ReadonlySet<string> = new Set();

/**
 * Promise cache: Arbor's staff list is the same for every org and barely
 * changes, so it is fetched once per browser session rather than once per Home
 * mount. Unlike the per-story caches, nothing about it goes stale on internal
 * navigation (pitfall 13), so it is deliberately never cleared.
 */
let request: Promise<ReadonlySet<string>> | null = null;

/**
 * Normalized display names of Arbor's own staff — everyone with an
 * `@tryarbor.com` email on their `PersonRecord`. Empty until loaded, and empty
 * when the lookup fails, so a failure hides nothing.
 *
 * Caveat: a clip stores only the editor's NAME, so this matches on the person
 * record's name. Staff whose Clerk display name differs from their person
 * record's name (which is auto-derived from the email local part when the
 * record is created) won't be matched; fixing the name in Dato fixes the match.
 */
export function useArborInternalEditorNames(): ReadonlySet<string> {
  const datoClientStore = useDatoClient();
  const [names, setNames] = useState<ReadonlySet<string>>(EMPTY);

  useEffect(() => {
    let cancelled = false;

    if (!request) {
      request = (async () => {
        try {
          const people =
            await datoClientStore.personRepository.findByEmailDomain(
              ARBOR_EMAIL_DOMAIN,
            );
          return toInternalEditorNameSet(people.map(person => person.name));
        } catch (err) {
          // Drop the failed promise so a later mount can retry.
          request = null;
          logger.warn(
            'Failed to load Arbor internal editors',
            extractErrorInfo(err),
          );
          return EMPTY;
        }
      })();
    }

    void request.then(loaded => {
      if (!cancelled) setNames(loaded);
    });

    return () => {
      cancelled = true;
    };
  }, [datoClientStore]);

  return names;
}
